/**
 * Rhodi Custom JS
 * - Hero parallax on scroll
 * - CountUp.js animated stats on viewport entry
 * - 72-hour rolling countdown timer (persisted via localStorage)
 * - AOS initialization
 * - FAQ accordion smooth height animation
 * - Legacy scroll animations (.rhodi-animate-in)
 */

(function () {
  'use strict';

  // ── AOS Init ───────────────────────────────────────────────────────────────
  function initAOS() {
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 700,
        easing: 'ease-out-cubic',
        once: true,
        offset: 80
      });
    }
  }

  // ── Hero Parallax ──────────────────────────────────────────────────────────
  // Moves the hero background slightly on scroll — never use background-attachment:fixed
  // (breaks on iOS Safari). Uses transform on the bg container instead.
  function initHeroParallax() {
    var heroBg = document.getElementById('rh-hero-bg');
    if (!heroBg) return;

    var hero = document.getElementById('rh-hero');
    if (!hero) return;

    window.addEventListener('scroll', function () {
      var scrollY = window.scrollY || window.pageYOffset;
      var heroBottom = hero.getBoundingClientRect().bottom + scrollY;
      if (scrollY > heroBottom) return; // don't apply below hero
      heroBg.style.transform = 'translateY(' + (scrollY * 0.30) + 'px)';
    }, { passive: true });
  }

  // ── CountUp Animated Stats ────────────────────────────────────────────────
  // Triggers CountUp.js on elements with data-countup attribute when they
  // enter the viewport at 30% threshold.
  function initCountUp() {
    if (typeof CountUp === 'undefined') return;

    var counters = document.querySelectorAll('[data-countup]');
    if (!counters.length) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !entry.target.classList.contains('rh-counted')) {
          entry.target.classList.add('rh-counted');
          var el = entry.target;
          var endVal = parseFloat(el.dataset.countup) || 0;
          var options = {
            duration: 2,
            suffix: el.dataset.suffix || '',
            prefix: el.dataset.prefix || '',
            decimalPlaces: parseInt(el.dataset.decimals, 10) || 0
          };
          var counter = new CountUp(el, endVal, options);
          if (!counter.error) counter.start();
        }
      });
    }, { threshold: 0.3 });

    counters.forEach(function (el) {
      observer.observe(el);
    });
  }

  // ── 72-Hour Rolling Countdown Timer ───────────────────────────────────────
  // Reads expiry from localStorage. If missing or past, resets to 72h from now.
  // This creates a rolling sense of urgency without faking scarcity.
  function initCountdown() {
    var hEl = document.getElementById('rh-timer-h');
    var mEl = document.getElementById('rh-timer-m');
    var sEl = document.getElementById('rh-timer-s');
    if (!hEl || !mEl || !sEl) return;

    var DURATION_MS = 72 * 60 * 60 * 1000; // 72 hours
    var STORAGE_KEY = 'rhodi_founder_expiry';

    var expiry = parseInt(localStorage.getItem(STORAGE_KEY), 10);
    var now = Date.now();

    if (!expiry || expiry < now) {
      expiry = now + DURATION_MS;
      try { localStorage.setItem(STORAGE_KEY, String(expiry)); } catch (e) {}
    }

    function pad(n) { return String(n).padStart(2, '0'); }

    function tick() {
      var remaining = expiry - Date.now();
      if (remaining <= 0) {
        // Reset for next cycle
        expiry = Date.now() + DURATION_MS;
        try { localStorage.setItem(STORAGE_KEY, String(expiry)); } catch (e) {}
        remaining = DURATION_MS;
      }
      var totalSecs = Math.floor(remaining / 1000);
      var hours = Math.floor(totalSecs / 3600);
      var mins  = Math.floor((totalSecs % 3600) / 60);
      var secs  = totalSecs % 60;
      hEl.textContent = pad(hours);
      mEl.textContent = pad(mins);
      sEl.textContent = pad(secs);
    }

    tick();
    setInterval(tick, 1000);
  }

  // ── Legacy Scroll Animations (.rhodi-animate-in) ───────────────────────────
  function initScrollAnimations() {
    var elements = document.querySelectorAll('.rhodi-animate-in');
    if (!elements.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    elements.forEach(function (el) { observer.observe(el); });
  }

  // ── FAQ Accordion ──────────────────────────────────────────────────────────
  function initFaqAccordion() {
    var items = document.querySelectorAll('.rhodi-faq__item details');
    if (!items.length) return;

    items.forEach(function (details) {
      var summary = details.querySelector('summary');
      var body = details.querySelector('.rhodi-faq__body');
      if (!summary || !body) return;

      if (details.open) {
        body.style.maxHeight = body.scrollHeight + 'px';
      }

      summary.addEventListener('click', function (e) {
        e.preventDefault();

        if (details.open) {
          body.style.maxHeight = body.scrollHeight + 'px';
          requestAnimationFrame(function () {
            body.style.maxHeight = '0';
          });
          body.addEventListener('transitionend', function () {
            details.open = false;
          }, { once: true });
        } else {
          details.open = true;
          body.style.maxHeight = '0';
          requestAnimationFrame(function () {
            body.style.maxHeight = body.scrollHeight + 'px';
          });
        }
      });
    });
  }

  function initNavSearch() {
    var nav = document.querySelector('.rhodi-nav');
    if (!nav || nav.dataset.searchReady) return;
    var toggle = nav.querySelector('.rhodi-nav__search-toggle');
    var panel = nav.querySelector('.rhodi-nav__mobile-search');
    var input = panel && panel.querySelector('input[type="search"]');
    if (!toggle || !panel || !input) return;
    nav.dataset.searchReady = 'true';
    var mobile = window.matchMedia('(max-width: 749px)');

    function setSearchOpen(open, restoreFocus) {
      var visible = !mobile.matches || open;
      if (!visible && panel.contains(document.activeElement)) {
        if (restoreFocus) toggle.focus({ preventScroll: true });
        else document.activeElement.blur();
      }
      nav.classList.toggle('is-search-open', mobile.matches && open);
      toggle.setAttribute('aria-expanded', String(mobile.matches && open));
      panel.inert = !visible;
      panel.setAttribute('aria-hidden', String(!visible));
      if (!open) panel.dispatchEvent(new CustomEvent('rhodi:search-close'));
      if (mobile.matches && open) input.focus({ preventScroll: true });
    }

    toggle.addEventListener('click', function () {
      if (mobile.matches) setSearchOpen(toggle.getAttribute('aria-expanded') !== 'true', true);
    });
    nav.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && nav.classList.contains('is-search-open')) {
        event.preventDefault();
        setSearchOpen(false, true);
      }
    });
    var hamburger = nav.querySelector('.rhodi-nav__hamburger');
    if (hamburger) hamburger.addEventListener('click', function () { setSearchOpen(false, false); });
    function resetSearch() { setSearchOpen(false, false); }
    mobile.addEventListener('change', resetSearch);
    var section = nav.closest('.shopify-section');
    if (section) section.addEventListener('shopify:section:unload', function () {
      mobile.removeEventListener('change', resetSearch);
    }, { once: true });
    resetSearch();
  }

  document.addEventListener('shopify:section:load', initNavSearch);

  // ── Init ───────────────────────────────────────────────────────────────────
  function init() {
    initNavSearch();
    initAOS();
    initHeroParallax();
    initCountUp();
    initCountdown();
    initScrollAnimations();
    initFaqAccordion();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
